import { MARKS, NODES } from "@repo/fika-blog/src/nodes"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import type { NodeSelection } from "@repo/fika-blog/src/tiptap/pm/state"
import { type Accessor, Show } from "solid-js"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip } from "@/designSystem/components/tooltip"
import { TransformButton } from "@/designSystem/patterns/editor-menu"
import { useI18nContext } from "@/i18n/i18n-solid"
import { HighlightSelect } from "./highlight-select"
import { useFormatToolbar } from "./use-format-toolbar"

interface FormatToolbarProps {
  editor: Accessor<Editor | undefined>
  showLinkOnActiveOnly?: boolean
  hideLink?: boolean
}

export function FormatToolbar(props: FormatToolbarProps) {
  const { LL } = useI18nContext()
  const {
    selection,
    hasTextSelection,
    markValues,
    linkValue,
    markOptions,
    markOnChange,
    linkOnChange,
    toggleList,
    isHighlightActive,
    isLinkActive,
  } = useFormatToolbar({ editor: props.editor })

  const showLink = () => {
    if (props.hideLink) return false
    if (props.showLinkOnActiveOnly) return hasTextSelection()
    return hasTextSelection() || isLinkActive()
  }

  return (
    <>
      <Show when={props.editor()}>
        {editor => (
          <TransformButton
            editor={editor()}
            selection={() => selection() as NodeSelection}
          />
        )}
      </Show>
      <Mini.ToggleGroupMultiple
        value={markValues()}
        onChange={markOnChange}
        values={markOptions()}
      />
      <Show when={isHighlightActive() && hasTextSelection()}>
        <Show when={props.editor()}>
          {editor => <HighlightSelect editor={editor()} />}
        </Show>
      </Show>
      <Mini.Divider />
      <Tooltip description={LL().editor.bulletList()}>
        <Mini.Button
          iconName={NODES.bulletList.iconName}
          onClick={() => toggleList("bulletList")}
        />
      </Tooltip>
      <Tooltip description={LL().editor.orderedList()}>
        <Mini.Button
          iconName={NODES.orderedList.iconName}
          onClick={() => toggleList("orderedList")}
        />
      </Tooltip>
      <Show when={showLink()}>
        <Mini.Divider />
        <Mini.ToggleGroup
          value={linkValue()}
          onChange={linkOnChange}
          values={[
            {
              value: "link",
              iconName: MARKS.link.iconName,
              title: LL().editor.link(),
            },
          ]}
        />
      </Show>
    </>
  )
}
